import './fixed-submit-bar.scss';
import {observer} from "mobx-react";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {Button} from "../../../components/inputs/button/button";
import {LABEL_POSITIONS, Toggle} from "../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {CreateRmaMainStore} from "../stores/create-rma-main-store";
import {IValidationGroup} from '../../../hooks/validation-group/validation-group-hook';
import {
    CREATE_RMA_SUBMIT_BLOCK_REASONS,
} from "../../manage-returns/rma-page/rma-types";
import currentAccountStore from '../../accounts/stores/current-account-store';
import currentPortalStore from '../../../stores/current-portal-store';
import { PERMISSION } from '../../../models/PermissionsTypes';
import { CREATE_RMA_MODE } from '../../../services/api/create-rma-service';
import BaseLayoutStickyToolbar
    from "../../../components/layouts/base-page-layout/base-layout-sticky-toolbar/base-layout-sticky-toolbar";
import { CreateRmaSubmitBlockTooltipWrapper } from './create-rma-paid-rma-tooltip';
import FontBody2 from '../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR } from '../../../components/fonts/font-props';


interface IProps {
    createRmaMainStore: CreateRmaMainStore;
    validtionGroup: IValidationGroup;
}

const BlockingReasonsList: React.FC<{
    createRmaMainStore: CreateRmaMainStore;
    pageScope: string;
    getTranslation: (key: string, scope: string, args?: string[]) => string;
}> = observer(({ createRmaMainStore, pageScope, getTranslation }) => {
    const reasons = createRmaMainStore.blockingReasons;

    if (!createRmaMainStore.isMixedOrder || reasons.length === 0) {
        return null;
    }

    return (
        <ul className="fixed-submit-bar-blocking-reasons">
            {reasons.map((reason) => (
                <li key={reason.draftIndex}>
                    <FontBody2 color={FONT_COLOR.ACCENT3}>
                        {getTranslation('FULFILLMENT_SECTION_TITLE', pageScope, [String(reason.draftIndex)])}
                        {': '}
                        {getTranslation(reason.reasonKey, pageScope)}
                    </FontBody2>
                </li>
            ))}
        </ul>
    );
});

const ButtonsGodMode: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isSubmitting = createRmaMainStore.isCreatingRma;
    const sessionStore = createRmaMainStore.session;
    const submittableDrafts = createRmaMainStore.submittableDrafts;
    const pageScope = 'dashboard-create-rma';
    const { getTranslation } = useDashboardTranslation();

    const isBlockedDueToCustomerPayment = submittableDrafts.some(
        (draft) => draft.requestStore.balancePreviewStore.isCreateRmaBlocked,
    );

    const shouldShowOutOfPolicyApproveTooltip = (): boolean => {
        const draftsWithItems = createRmaMainStore.draftsWithSelectedItems;
        if (draftsWithItems.length === 0) {
            return false;
        }

        if (!currentPortalStore.requireManagerApproval()) {
            return false;
        }

        if (!draftsWithItems.some((draft) => draft.requestStore.eligibilityStore.isOutOfPolicy)) {
            return false;
        }

        if (currentAccountStore.hasPermission(PERMISSION.APPROVE_RMA_AS_MANAGER)
            || currentAccountStore.hasPermission(PERMISSION.PROMPT_MANGER_APPROVAL)) {
            return false;
        }

        return true;
    };

    const getApproveSubmitBlockReason = (): CREATE_RMA_SUBMIT_BLOCK_REASONS | undefined => {
        if (isBlockedDueToCustomerPayment) {
            return CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED;
        }
        if (shouldShowOutOfPolicyApproveTooltip()) {
            return CREATE_RMA_SUBMIT_BLOCK_REASONS.OUT_OF_POLICY_NO_APPROVE_PERMISSION;
        }
        return undefined;
    };

    const shouldShowDisabledButtonWithTooltip = (): boolean => {
        return getApproveSubmitBlockReason() !== undefined;
    };

    const disableSubmitButton = !createRmaMainStore.canSubmit
        || !validtionGroup.isValid();
    const approveSubmitBlockReason = getApproveSubmitBlockReason();
    const pendingSubmitBlockReason = isBlockedDueToCustomerPayment
        ? CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED
        : undefined;

    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={pendingSubmitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA_AS_PENDING', pageScope)}
                    disabled={disableSubmitButton}
                    hasLoader={true}
                    isLoading={isSubmitting && !sessionStore?.isAutoApprove}
                    onClick={() => {
                        sessionStore?.setIsAutoApprove(false);
                        createRmaMainStore.submit();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={approveSubmitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA_AS_APPROVED', pageScope)}
                    disabled={shouldShowDisabledButtonWithTooltip() ? true : disableSubmitButton}
                    hasLoader={true}
                    isLoading={isSubmitting && sessionStore?.isAutoApprove}
                    onClick={() => {
                        sessionStore?.setIsAutoApprove(true);
                        createRmaMainStore.submit();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
        </span>
    );
});

const ButtonPolicyLed: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isSubmitting = createRmaMainStore.isCreatingRma;
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const submittableDrafts = createRmaMainStore.submittableDrafts;

    const isBlockedDueToCustomerPayment = submittableDrafts.some(
        (draft) => draft.requestStore.balancePreviewStore.isCreateRmaBlocked,
    );

    const shouldDisableSubmitButton = !createRmaMainStore.canSubmit
        || !validtionGroup.isValid();

    const submitBlockReason = isBlockedDueToCustomerPayment
        ? CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED
        : undefined;

    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={submitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA', pageScope)}
                    disabled={shouldDisableSubmitButton}
                    hasLoader={true}
                    isLoading={isSubmitting}
                    onClick={() => {
                        createRmaMainStore.session?.setIsAutoApprove(false);
                        createRmaMainStore.submit();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
        </span>
    );
});


const FixedSubmitBar: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const sessionStore = createRmaMainStore.session;
    const isCustomerEmailMissing = !createRmaMainStore.orderStore?.customer?.email;

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';

    return (
        <BaseLayoutStickyToolbar>
            <div className="fixed-submit-bar">
                <BlockingReasonsList
                    createRmaMainStore={createRmaMainStore}
                    pageScope={pageScope}
                    getTranslation={getTranslation}
                />
                <span>
                    <Toggle label={getTranslation('SEND_CUSTOMER_EMAILS', pageScope)}
                            checked={sessionStore?.sendCustomerEmails}
                            onChange={sessionStore?.setSendCustomerEmail}
                            labelPosition={LABEL_POSITIONS.LEFT}
                            disabled={isCustomerEmailMissing}/>
                </span>

                { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE &&
                    <ButtonsGodMode createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} />
                }
                { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE &&
                    <ButtonPolicyLed createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} />
                }
            </div>
        </BaseLayoutStickyToolbar>
    );
});

export default FixedSubmitBar;
